import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { campaignService } from '../services/campaignService';
import { networkService } from '../services/networkService';
import PasskeyManager from './PasskeyManager';
import InterestSelection from './InterestSelection';

export const Profile = () => {
  const { currentUser, updateProfile, logout } = useAuth();
  const [userCampaigns, setUserCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    displayName: '',
    email: ''
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [inviteCode, setInviteCode] = useState(null);
  const [inviteCodeLoading, setInviteCodeLoading] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(null);
  const [networkStats, setNetworkStats] = useState(null);
  const [networkStatsLoading, setNetworkStatsLoading] = useState(false);
  const [cancellingCampaign, setCancellingCampaign] = useState(null);
  const [interests, setInterests] = useState([]);
  const [editingInterests, setEditingInterests] = useState(false);

  useEffect(() => {
    if (currentUser) {
      setFormData({
        displayName: currentUser.displayName || '',
        email: currentUser.email || ''
      });

      fetchUserCampaigns();
      fetchInviteCode();
      fetchNetworkStats();
      fetchUserInterests();
    }
  }, [currentUser]);

  // Load Monetag interstitial ad script
  useEffect(() => {
    const script = document.createElement('script');
    script.dataset.zone = '10110392';
    script.src = 'https://groleegni.net/vignette.min.js';
    script.async = true;

    const targetElement = [document.documentElement, document.body].filter(Boolean).pop();
    if (targetElement) {
      targetElement.appendChild(script);
    }

    // Cleanup function to remove the script when component unmounts
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const fetchUserCampaigns = async () => {
    try {
      setIsLoading(true);
      const response = await campaignService.getUserCampaigns();
      
      // The API returns the campaigns directly as an array
      if (Array.isArray(response)) {
        setUserCampaigns(response);
      } else {
        setError('Failed to fetch campaigns');
      }
    } catch (err) {
      setError('An error occurred while fetching campaigns');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchInviteCode = async () => {
    console.log('=== FETCH INVITE CODE DEBUG ===');
    try {
      setInviteCodeLoading(true);
      
      // Get token from localStorage
      const token = localStorage.getItem('sbwom_token') || sessionStorage.getItem('sbwom_token');
      console.log('Token found:', token ? 'YES' : 'NO');
      
      if (token) {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const apiUrl = `${API_BASE_URL}/network/invite`;
        console.log('Fetching from:', apiUrl);
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        
        console.log('Response status:', response.status);
        console.log('Response ok:', response.ok);
        
        const data = await response.json();
        console.log('Response data:', data);
        
        if (data && data.inviteCode) {
          console.log('Setting inviteCode:', data.inviteCode);
          setInviteCode(data.inviteCode);
          const inviteUrl = `${window.location.origin}/register?inviteCode=${data.inviteCode}`;
          console.log('Setting inviteLink:', inviteUrl);
          setInviteLink(inviteUrl);
        } else {
          console.log('No invite code in response');
        }
      } else {
        console.log('No token found - cannot fetch invite code');
      }
    } catch (err) {
      console.error('Failed to fetch invite code:', err);
      console.error('Error details:', {
        name: err.name,
        message: err.message,
        stack: err.stack
      });
    } finally {
      setInviteCodeLoading(false);
    }
  };

  const fetchNetworkStats = async () => {
    try {
      setNetworkStatsLoading(true);
      const data = await networkService.getNetworkCluster();
      console.log('Network data received in Profile:', data);

      // Calculate stats from the network data (same logic as NetworkTree.js)
      let directInvitees = 0;
      let indirectInvitees = 0;
      let totalConnections = 0;
      let maxLevel = 0;

      const countChildren = (node, level) => {
        if (level > maxLevel) maxLevel = level;
        const children = node.children || [];

        for (const child of children) {
          totalConnections++;
          if (level === 0) {
            directInvitees++;
            countChildren(child, level + 1);
          } else {
            indirectInvitees++;
            countChildren(child, level + 1);
          }
        }
      };

      // Count from all roots
      const roots = data.roots || [];
      for (const root of roots) {
        countChildren(root, 0);
      }

      setNetworkStats({
        directInvitees,
        indirectInvitees,
        treeMerges: data.treeMergesCount || 0,
        totalConnections,
        networkDepth: maxLevel + 1
      });
    } catch (err) {
      console.error('Failed to fetch network stats:', err);
    } finally {
      setNetworkStatsLoading(false);
    }
  };

  const fetchUserInterests = async () => {
    try {
      const token = localStorage.getItem('sbwom_token') || sessionStorage.getItem('sbwom_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setInterests(data.interests || []);
      }
    } catch (err) {
      console.error('Failed to fetch user interests:', err);
    }
  };

  const copyInviteCode = () => {
    if (!inviteCode) {
      alert('No invite code available to copy.');
      return;
    }

    navigator.clipboard.writeText(inviteCode)
      .then(() => {
        setCopiedCode(true);
        setTimeout(() => setCopiedCode(false), 2000);
      })
      .catch(err => {
        console.error('ERROR copying invite code:', err);
        alert('Failed to copy invite code. Please copy manually.');
      });
  };

  const copyInviteLink = () => {
    if (!inviteLink) {
      alert('No invite link available to copy. Please try generating an invite code first.');
      return;
    }

    navigator.clipboard.writeText(inviteLink)
      .then(() => {
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
      })
      .catch(err => {
        console.error('ERROR copying invite link:', err);
        alert('Failed to copy invite link. Please copy manually.');
      });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear messages when user changes input
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const result = await updateProfile(formData);
      
      if (result.success) {
        setSuccess('Profile updated successfully');
        setIsEditing(false);
      } else {
        setError(result.message || 'Failed to update profile');
      }
    } catch (err) {
      setError('An error occurred while updating your profile');
      console.error(err);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);

    // Validate passwords match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    // Validate password length
    if (passwordData.newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return;
    }

    try {
      const token = localStorage.getItem('sbwom_token') || sessionStorage.getItem('sbwom_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setPasswordSuccess('Password changed successfully');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        setPasswordError(data.error || 'Failed to change password');
      }
    } catch (err) {
      setPasswordError('An error occurred while changing password');
      console.error(err);
    }
  };

  const handlePasswordFieldChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogout = async () => {
    try {
      await logout();
      // Redirect to home page after successful logout
      window.location.href = '/';
    } catch (err) {
      setError('An error occurred while logging out');
      console.error(err);
    }
  };

  const handleCancelCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to pause this campaign? You can create a new one afterwards.')) {
      return;
    }

    try {
      setCancellingCampaign(campaignId);
      await campaignService.cancelCampaign(campaignId);
      setSuccess('Campaign paused successfully. You can now create a new campaign.');
      // Refresh the campaigns list
      await fetchUserCampaigns();
    } catch (err) {
      setError('Failed to pause campaign. Please try again.');
      console.error(err);
    } finally {
      setCancellingCampaign(null);
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (!currentUser) {
    return (
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6 mt-8">
        <div className="text-center">
          <p className="text-brown-600">Please log in to view your profile.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 h-full flex flex-col main-container" style={{ overflow: 'auto', backgroundColor: 'transparent' }}>
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-brown-800">My Profile</h1>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded mb-3">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-3 py-2 rounded mb-3">
          {success}
        </div>
      )}

      {/* User Profile Section */}
      <div className="flex items-center mb-4">
        <div className="w-12 h-12 bg-brown-500 rounded-full flex items-center justify-center text-white text-lg font-bold mr-3">
          {currentUser.displayName ? currentUser.displayName.charAt(0).toUpperCase() : 'U'}
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-brown-900">
            {currentUser.displayName || 'User'}
          </h2>
          <p className="text-sm text-brown-600">{currentUser.email}</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="bg-brown-600 hover:bg-brown-700 text-white font-bold py-1 px-3 rounded-full shadow-lg text-sm"
          >
            {isEditing ? 'Cancel' : 'Edit Profile'}
          </button>
          <button
            onClick={handleLogout}
            className="bg-red-600 hover:bg-red-700 text-white font-bold py-1 px-3 rounded-full shadow-lg text-sm"
          >
            Log Out
          </button>
        </div>
      </div>

      {isEditing && (
        <form onSubmit={handleSubmit} className="mb-4 bg-brown-50 p-3 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label htmlFor="displayName" className="block text-brown-700 text-sm font-bold mb-1">
                Name
              </label>
              <input
                type="text"
                id="displayName"
                name="displayName"
                className="shadow appearance-none border rounded w-full py-1 px-2 text-brown-700 leading-tight focus:outline-none focus:shadow-outline focus:border-brown-500 text-sm"
                value={formData.displayName}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-brown-700 text-sm font-bold mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                className="shadow appearance-none border rounded w-full py-1 px-2 text-brown-700 leading-tight focus:outline-none focus:shadow-outline focus:border-brown-500 text-sm"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
          </div>
          <div className="mt-3">
            <button
              type="submit"
              className="bg-brown-600 hover:bg-brown-700 text-white font-bold py-1 px-3 rounded-full shadow-lg text-sm"
            >
              Save Changes
            </button>
          </div>
        </form>
      )}

      {/* Security Section - appears when editing */}
      {isEditing && (
        <div className="mb-4 space-y-4">
          {/* Passkey Security Card */}
          <div className="bg-white rounded-xl shadow-lg p-4">
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 bg-brown-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-2xl">🔐</span>
              </div>
              <div>
                <h3 className="text-lg font-bold text-brown-900">Security Settings</h3>
                <p className="text-sm text-brown-600">Manage your authentication methods</p>
              </div>
            </div>

            {/* Passkey Upgrade Banner - Only show if user has a password */}
            {currentUser?.hasPassword && (
              <div className="bg-brown-50 border border-brown-300 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <span className="text-3xl">✨</span>
                  </div>
                  <div className="ml-3 flex-1">
                    <h4 className="text-sm font-bold text-brown-900 mb-1">
                      Upgrade to Passwordless Authentication
                    </h4>
                    <p className="text-sm text-brown-700 mb-3">
                      Say goodbye to passwords! Use Face ID, Touch ID, or Windows Hello for secure, convenient access to your account.
                    </p>
                    <Link
                      to="/upgrade-to-passkey?reason=security"
                      className="inline-block bg-brown-600 hover:bg-brown-700 text-white font-bold py-2 px-4 rounded-lg text-sm transition-colors duration-300"
                    >
                      Switch to Passkeys
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Passkey Manager Component */}
            <PasskeyManager />
          </div>

          {/* Legacy Password Change - Only for users with passwords */}
          {currentUser?.hasPassword && (
            <div className="bg-white rounded-xl shadow-lg p-4">
              <h3 className="text-lg font-bold text-brown-900 mb-3">Change Password (Legacy)</h3>

              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 mb-3">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> We recommend switching to passkeys instead of managing passwords. It's more secure and convenient!
                </p>
              </div>

              {passwordError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded mb-3 text-sm">
                  {passwordError}
                </div>
              )}

              {passwordSuccess && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-3 py-2 rounded mb-3 text-sm">
                  {passwordSuccess}
                </div>
              )}

              <form onSubmit={handlePasswordChange}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label htmlFor="currentPassword" className="block text-brown-700 text-sm font-bold mb-1">
                      Current Password
                    </label>
                    <input
                      type="password"
                      id="currentPassword"
                      name="currentPassword"
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-brown-700 leading-tight focus:outline-none focus:shadow-outline focus:border-brown-500 text-sm"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordFieldChange}
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="newPassword" className="block text-brown-700 text-sm font-bold mb-1">
                      New Password
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-brown-700 leading-tight focus:outline-none focus:shadow-outline focus:border-brown-500 text-sm"
                      value={passwordData.newPassword}
                      onChange={handlePasswordFieldChange}
                      required
                      minLength="6"
                    />
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-brown-700 text-sm font-bold mb-1">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-brown-700 leading-tight focus:outline-none focus:shadow-outline focus:border-brown-500 text-sm"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordFieldChange}
                      required
                      minLength="6"
                    />
                  </div>
                </div>

                <div className="mt-3">
                  <button
                    type="submit"
                    className="bg-brown-600 hover:bg-brown-700 text-white font-bold py-2 px-4 rounded-full shadow-lg text-sm"
                  >
                    Update Password
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {/* My Interests Section */}
      <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
              <span className="text-2xl">🎯</span>
            </div>
            <div>
              <h3 className="text-lg font-bold text-brown-900">My Interests</h3>
              <p className="text-sm text-brown-600">Personalize your campaign feed</p>
            </div>
          </div>
          <button
            onClick={() => setEditingInterests(true)}
            className="bg-brown-600 hover:bg-brown-700 text-white font-bold py-2 px-4 rounded-lg text-sm transition-colors duration-300"
          >
            {interests.length > 0 ? 'Edit Interests' : 'Add Interests'}
          </button>
        </div>

        {interests.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {interests.map(interest => (
              <span
                key={interest}
                className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium capitalize"
              >
                {interest}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-brown-600 text-sm">
            No interests selected yet. Add your interests to get personalized campaign recommendations!
          </p>
        )}
      </div>

      {/* Three Horizontal Cards */}
      <div className="flex-1 overflow-hidden">
        <div className="profile-card-grid grid grid-cols-1 md:grid-cols-3 gap-3 md:h-[85%]">
          {/* Invite Friends Card */}
          <div className="campaign-card profile-card profile-card--compact bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-xl">
            <div className="profile-card-body p-3 md:p-4 h-full flex flex-col">
              <div className="flex items-center mb-2">
                <div className="w-7 h-7 md:w-8 md:h-8 bg-green-100 rounded-full flex items-center justify-center mr-2">
                  <svg className="w-4 h-4 md:w-4 md:h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-sm font-bold text-green-800">Invite Friends</h2>
                  <p className="text-xs text-green-500">Grow Your Network</p>
                </div>
              </div>
              
              <p className="text-green-700 mb-2 line-clamp-2 text-xs">
                Share your invite code with friends to grow your network! When someone registers with your code, they'll be added to your network.
              </p>
              
              {inviteCodeLoading ? (
                <div className="flex items-center justify-center py-1.5 md:py-2">
                  <div className="spinner-border animate-spin inline-block w-4 h-4 border-2 rounded-full text-green-600" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <span className="ml-1 text-green-600 text-xs">Generating...</span>
                </div>
              ) : inviteCode ? (
                <div className="mt-auto">
                  <div className="mb-1.5 md:mb-2">
                    <label className="block text-green-700 text-xs font-bold mb-1">
                      Your Invite Code:
                    </label>
                    <div className="flex items-center">
                      <div className="bg-white border border-green-300 rounded px-2 py-0.5 md:py-1 font-mono text-xs text-green-700 flex-grow">
                        {inviteCode}
                      </div>
                      <button
                        onClick={copyInviteCode}
                        className="ml-1 bg-green-600 hover:bg-green-700 text-white font-bold py-0.5 md:py-1 px-2 rounded text-xs focus:outline-none focus:shadow-outline"
                      >
                        {copiedCode ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>

                  <div className="mb-1.5 md:mb-2">
                    <label className="block text-green-700 text-xs font-bold mb-1">
                      Or share this link:
                    </label>
                    <div className="flex items-center">
                      <input
                        type="text"
                        value={inviteLink}
                        readOnly
                        className="bg-white border border-green-300 rounded px-2 py-0.5 md:py-1 text-xs text-green-700 flex-grow"
                      />
                      <button
                        onClick={copyInviteLink}
                        className="ml-1 bg-green-600 hover:bg-green-700 text-white font-bold py-0.5 md:py-1 px-2 rounded text-xs focus:outline-none focus:shadow-outline"
                      >
                        {copiedLink ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-1.5 md:py-2 mt-auto">
                  <button
                    onClick={copyInviteLink}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold py-1 px-3 rounded-full text-xs focus:outline-none focus:shadow-outline"
                  >
                    {copiedLink ? 'Copied!' : 'Copy Invite Link'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* My Campaign Card */}
          <div className="campaign-card profile-card profile-card--compact bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-xl">
            <div className="profile-card-body p-3 md:p-4 h-full flex flex-col">
              <div className="flex items-center mb-2">
                <div className="w-7 h-7 md:w-8 md:h-8 bg-brown-100 rounded-full flex items-center justify-center mr-2">
                  <svg className="w-4 h-4 md:w-4 md:h-4 text-brown-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-sm font-bold text-brown-800">My Campaign</h2>
                  <p className="text-xs text-brown-500">Campaign Details</p>
                </div>
              </div>
              
              {isLoading ? (
                <div className="flex justify-center items-center h-20 md:h-24">
                  <div className="text-center">
                    <div className="spinner-border animate-spin inline-block w-5 h-5 border-3 rounded-full text-brown-600" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <p className="mt-1 text-brown-600 text-xs">Loading campaign...</p>
                  </div>
                </div>
              ) : userCampaigns.length === 0 ? (
                <div className="text-center py-2 md:py-3 mt-auto">
                  <p className="text-brown-600 text-xs mb-2">You haven't created any campaigns yet.</p>
                  <a
                    href="/create-campaign"
                    className="bg-brown-600 hover:bg-brown-700 text-white font-bold py-1 px-3 rounded-full text-xs whitespace-nowrap"
                  >
                    Create Your First Campaign
                  </a>
                </div>
              ) : (
                <div className="mt-auto">
                  {userCampaigns.slice(0, 1).map((campaign) => (
                    <div key={campaign.id}>
                      <h3 className="font-medium text-brown-900 mb-1 text-xs">{campaign.title}</h3>
                      <p className="text-brown-600 text-xs mb-1.5 md:mb-2 line-clamp-2">{campaign.description}</p>
                      <div className="flex items-center justify-between text-xs text-brown-500 mb-1.5 md:mb-2">
                        <div className="flex items-center">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                          </svg>
                          {formatDate(campaign.createdAt)}
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          campaign.status === 'active' ? 'bg-green-100 text-green-800' :
                          campaign.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {campaign.status}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <a
                          href={`/campaigns/${campaign.id}`}
                          className="flex-1 bg-brown-600 hover:bg-brown-700 text-white font-medium py-1 px-2 rounded-full text-xs transition-colors duration-300 text-center"
                        >
                          View
                        </a>
                        {campaign.status === 'active' && (
                          <button
                            onClick={() => handleCancelCampaign(campaign.id)}
                            disabled={cancellingCampaign === campaign.id}
                            className="flex-1 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white font-medium py-1 px-2 rounded-full text-xs transition-colors duration-300"
                          >
                            {cancellingCampaign === campaign.id ? 'Pausing...' : 'Pause'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Network Stats Card */}
          <div className="campaign-card profile-card profile-card--compact bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-xl">
            <div className="profile-card-body p-3 md:p-4 h-full flex flex-col">
              <div className="flex items-center mb-2">
                <div className="w-7 h-7 md:w-8 md:h-8 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                  <svg className="w-4 h-4 md:w-4 md:h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-sm font-bold text-blue-800">Network Stats</h2>
                  <p className="text-xs text-blue-500">Your Connections</p>
                </div>
              </div>
              
              <p className="text-blue-700 mb-2 line-clamp-2 text-xs">
                Track your network growth and connections. See how many people you've invited and joined your network.
              </p>

              {networkStatsLoading ? (
                <div className="flex justify-center items-center h-20 md:h-24 mt-auto">
                  <div className="text-center">
                    <div className="spinner-border animate-spin inline-block w-5 h-5 border-3 rounded-full text-blue-600" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <p className="mt-1 text-blue-600 text-xs">Loading stats...</p>
                  </div>
                </div>
              ) : (
                <div className="mt-auto">
                  <div className="grid grid-cols-2 gap-2 mb-1.5 md:mb-2">
                    <div className="bg-green-50 p-1.5 md:p-2 rounded-lg border border-green-200">
                      <div className="text-sm font-bold text-green-800">{networkStats?.directInvitees || 0}</div>
                      <div className="text-xs text-green-700">Direct</div>
                    </div>
                    <div className="bg-blue-50 p-1.5 md:p-2 rounded-lg border border-blue-200">
                      <div className="text-sm font-bold text-blue-800">{networkStats?.indirectInvitees || 0}</div>
                      <div className="text-xs text-blue-700">Indirect</div>
                    </div>
                    <div className="bg-purple-50 p-1.5 md:p-2 rounded-lg border border-purple-200">
                      <div className="text-sm font-bold text-purple-800">{networkStats?.treeMerges || 0}</div>
                      <div className="text-xs text-purple-700">Tree Merges</div>
                    </div>
                    <div className="bg-orange-50 p-1.5 md:p-2 rounded-lg border border-orange-200">
                      <div className="text-sm font-bold text-orange-800">{networkStats?.totalConnections || 0}</div>
                      <div className="text-xs text-orange-700">Total</div>
                    </div>
                    <div className="bg-indigo-50 p-1.5 md:p-2 rounded-lg border border-indigo-200 col-span-2">
                      <div className="text-sm font-bold text-indigo-800">{networkStats?.networkDepth || 0}</div>
                      <div className="text-xs text-indigo-700">Network Depth</div>
                    </div>
                  </div>
                  <a
                    href="/network"
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-1 px-3 rounded-full text-xs transition-colors duration-300 w-full text-center block"
                  >
                    View Network
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Interest Selection Modal */}
      {editingInterests && (
        <InterestSelection
          onComplete={() => {
            setEditingInterests(false);
            fetchUserInterests();
          }}
          onSkip={() => setEditingInterests(false)}
        />
      )}
    </div>
  );
};

export default Profile;
